import { getSupplierSpaceUsageByPage } from '../../requests/reports/space-usage/supplier-space-usage-requests';
import { requestCalculateBuyerSpaceUsageSummary } from '../../requests/reports/space-usage/buyer-space-usage-requests';
import { requestAdminSpaceUsageSummary } from '../../requests/reports/space-usage/admin-space-usage-requests';
import { coerceCount } from '../../requests/generic-requests';
import { isoDateRange } from './requests/apiDates';
import { hasPeriodEnded } from './periodParser';
import type { AssistantAudience } from './assistantCapabilities';
import type { KnowledgeAnswer, KnowledgeContext, KnowledgeQuery } from './knowledgeRegistry';

/**
 * Answers how many overages were charged over a period.
 *
 * ## What an "overage" is here, because the unit is easy to get wrong
 *
 * The figure comes from `booked_space_calendar`, where one row is **one buyer,
 * at one location, on one day**. `overageSpacesChargedCount` on that row is how
 * many spaces beyond the booked amount were billed *that day*. The reported
 * total is the sum of those rows, so its unit is **space-days**: three extra
 * trucks parked for ten days is 30, not 3 and not 1.
 *
 * That distinction is the whole reason this skill states it in the answer. "You
 * charged 30 overages" invites the reading "30 separate incidents", which would
 * be wrong by an order of magnitude and completely plausible. The server-side
 * aggregate is literally `SUM(overage_spaces_charged_count)` — see
 * `BookedSpaceCalendarViewRepositoryCustomImpl.aggregateSpaceUsageSummary`.
 *
 * ## Why three sources
 *
 * The API has a single-call summary aggregate for admins and buyers, but **not
 * for suppliers** — a supplier only has the paginated search. So a supplier's
 * total is summed from rows here, using the same fields the server sums, while
 * the other two audiences use the aggregate rather than re-deriving it badly.
 */

/**
 * Rows per request on the supplier path. The API rejects anything above 100
 * ("Page size must be between 1 and 100", code 115003).
 */
const PAGE_SIZE = 100;

/**
 * Hard ceiling on requests for one question. A supplier's rows multiply out as
 * buyers x locations x days, so a wide window could otherwise fire an unbounded
 * burst at the API.
 */
const MAX_PAGES = 50;

interface OverageTotals {
  charged: number;
  uncharged: number;
  total: number;
  /**
   * True when the page ceiling stopped us before every row was read. Unlike a
   * ranking, a partial sum is not merely imprecise — it is guaranteed too low,
   * so the answer must not present it as the figure.
   */
  truncated: boolean;
  rowsRead: number;
  rowsAvailable: number;
}

const EMPTY: OverageTotals = {
  charged: 0,
  uncharged: 0,
  total: 0,
  truncated: false,
  rowsRead: 0,
  rowsAvailable: 0,
};

/** A row of the supplier space-usage search. */
interface SpaceUsageRow {
  overageSpaces?: number;
  overageSpacesChargedCount?: number;
  unchargedOverages?: number;
}

const numeric = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

/** The server's own summary shape, returned by the buyer and admin aggregates. */
interface SpaceUsageSummaryBody {
  totalOverageSpaces?: number;
  totalChargedOverages?: number;
  totalUnchargedOverages?: number;
}

const fromSummary = (body: SpaceUsageSummaryBody | undefined): OverageTotals => {
  const charged = numeric(body?.totalChargedOverages);
  const uncharged = numeric(body?.totalUnchargedOverages);
  return {
    charged,
    uncharged,
    /*
     * Derived rather than read from totalOverageSpaces. The two are the same
     * number when the data is consistent, and deriving it means the three
     * figures shown to the user always add up, which is what a reader checks.
     */
    total: charged + uncharged,
    truncated: false,
    rowsRead: 0,
    rowsAvailable: 0,
  };
};

interface OverageQueryParams {
  startDate: string;
  endDate: string;
  locationId?: string;
}

/** Sums a supplier's rows, because no supplier-side aggregate endpoint exists. */
const collectSupplierOverages = async (params: OverageQueryParams): Promise<OverageTotals> => {
  const totals = { ...EMPTY };
  let page = 0;

  while (page < MAX_PAGES) {
    const response = await getSupplierSpaceUsageByPage({ ...params, page, size: PAGE_SIZE });
    const body = (response?.body ?? {}) as { content?: SpaceUsageRow[] };
    totals.rowsAvailable = coerceCount(response?.body) ?? totals.rowsAvailable;

    const rows = Array.isArray(body.content) ? body.content : [];
    if (rows.length === 0) break;

    rows.forEach((row) => {
      totals.charged += numeric(row.overageSpacesChargedCount);
      totals.uncharged += numeric(row.unchargedOverages);
      totals.total += numeric(row.overageSpaces);
    });

    totals.rowsRead += rows.length;
    page += 1;
    if (totals.rowsRead >= totals.rowsAvailable) break;
  }

  totals.truncated = totals.rowsAvailable > totals.rowsRead;
  return totals;
};

const collectBuyerOverages = async (params: OverageQueryParams): Promise<OverageTotals> => {
  // This request module formats its own dates, so it takes the ISO window.
  const response = await requestCalculateBuyerSpaceUsageSummary(params);
  return fromSummary(response?.body as SpaceUsageSummaryBody | undefined);
};

const collectAdminOverages = async (params: OverageQueryParams): Promise<OverageTotals> => {
  const response = await requestAdminSpaceUsageSummary(params);
  return fromSummary(response?.body as SpaceUsageSummaryBody | undefined);
};

const count = (value: number): string => value.toLocaleString();

/**
 * The clarifier that stops a correct number being read as the wrong thing.
 * Omitted at zero, where there is no unit to misread.
 */
const UNIT_NOTE =
  'Each one counts a single space over the booked amount for a single day, so a ' +
  'few extra trucks parked for a week adds up quickly.';

const describe = (
  totals: OverageTotals,
  audience: AssistantAudience,
  label: string,
  scopePhrase: string,
  past: boolean,
): KnowledgeAnswer => {
  const verb = past ? 'were' : 'have been';

  if (totals.truncated) {
    return {
      text:
        `I can only read ${count(totals.rowsRead)} of the ${count(totals.rowsAvailable)} ` +
        `space-usage records ${label}, so any total I gave you would be too low rather ` +
        'than merely imprecise. Ask about a shorter period, or a single location, and I ' +
        'can give you a figure I can stand behind.',
    };
  }

  if (totals.total === 0) {
    return {
      text: `No overages ${verb} recorded ${scopePhrase} ${label}, so nothing ${verb} charged.`,
    };
  }

  const subject = audience === 'admin' ? 'Across the platform' : `${scopePhrase[0].toUpperCase()}${scopePhrase.slice(1)}`;
  const opening =
    audience === 'admin'
      ? `${subject}, **${count(totals.charged)}** of ${count(totals.total)} overages ${verb} charged ${label}.`
      : `**${count(totals.charged)}** of ${count(totals.total)} overages ${scopePhrase} ${verb} charged ${label}.`;

  return {
    text: `${opening} ${UNIT_NOTE}`,
    breakdown: [
      { label: 'Charged', value: count(totals.charged) },
      { label: 'Not charged', value: count(totals.uncharged) },
      { label: 'Total overages', value: count(totals.total) },
    ],
  };
};

const SYNONYMS = [
  'how many total overages were charged',
  'how many overages were charged',
  'how many overages did i charge',
  'how many overages was i charged',
  'how many charged overages',
  'total charged overages',
  'how many overage spaces were charged',
  'how many overages were billed',
  'how many overages',
  'total overages',
];

/**
 * Concepts for the phrasings the synonym list cannot enumerate. "overage" is
 * the anchor and is specific enough to carry the match on its own alongside a
 * quantity word, so the second group stays broad.
 *
 * Deliberately no "charge"-only group: "how many overages did I have" is the
 * same question and never says charged, and the answer reports both figures
 * anyway.
 */
const REQUIRES = [
  ['overage', 'overages'],
  ['many', 'total', 'number', 'count', 'much'],
];

const DESCRIPTIONS: Record<AssistantAudience, string> = {
  supplier:
    'How many overages were charged at the supplier\u2019s own locations over a time ' +
    'period, alongside how many went uncharged. An overage is one space beyond the ' +
    'booked amount on one day, so the figure is space-days rather than a count of ' +
    'incidents. Use for "how many overages were charged" style questions. Not a ' +
    'money figure \u2014 it does not say what the overages were worth.',
  buyer:
    'How many overages the buyer was charged for over a time period, alongside how ' +
    'many went uncharged. An overage is one space beyond the booked amount on one ' +
    'day, so the figure is space-days rather than a count of incidents. Use for ' +
    '"how many overages was I charged" style questions. Not a money figure.',
  admin:
    'How many overages were charged across the whole platform over a time period, ' +
    'alongside how many went uncharged. An overage is one space beyond the booked ' +
    'amount on one day, so the figure is space-days rather than a count of ' +
    'incidents. Use for platform-wide "how many overages were charged" questions. ' +
    'Not a money figure and not scoped to one supplier.',
};

const COLLECTORS: Record<AssistantAudience, (params: OverageQueryParams) => Promise<OverageTotals>> = {
  supplier: collectSupplierOverages,
  buyer: collectBuyerOverages,
  admin: collectAdminOverages,
};

/*
 * Every one of these request modules runs its own moment().format(), so all
 * three take the ISO window. Handing one an already-formatted date re-parses it
 * through moment's deprecated fallback to `new Date()` — which happens to work
 * today and is exactly the kind of silent brittleness that put a malformed date
 * on a live server once already. See the note on isoDateRange.
 */
const rangeFor = isoDateRange;

const buildOveragesQuery = (audience: AssistantAudience): KnowledgeQuery => ({
  id: `${audience}OveragesCharged`,
  audiences: [audience],
  synonyms: SYNONYMS,
  requires: REQUIRES,
  example: 'how many overages were charged last month',
  description: DESCRIPTIONS[audience],
  answer: async (context: KnowledgeContext): Promise<KnowledgeAnswer> => {
    const { period, targets, wholeAccount } = context;
    const past = hasPeriodEnded(period);

    /*
     * The endpoint filters on a single locationId, so a question naming several
     * locations is answered account-wide rather than for an arbitrary one of
     * them. Saying which scope was used matters more than silently picking.
     */
    const single = !wholeAccount && targets.length === 1 ? targets[0] : null;
    const scopePhrase = single
      ? `at **${single.name}**`
      : audience === 'buyer'
        ? 'across your bookings'
        : 'across your locations';

    const totals = await COLLECTORS[audience]({
      ...rangeFor(period),
      ...(single ? { locationId: single.id } : {}),
    });

    const answer = describe(totals, audience, period.label, scopePhrase, past);

    if (!single && !wholeAccount && targets.length > 1) {
      return {
        ...answer,
        text:
          `${answer.text} That covers every location \u2014 I can only filter overages ` +
          'one location at a time, so ask about a single one for its own figure.',
      };
    }

    return answer;
  },
});

export const supplierOveragesQuery = buildOveragesQuery('supplier');
export const buyerOveragesQuery = buildOveragesQuery('buyer');
export const adminOveragesQuery = buildOveragesQuery('admin');

export const __testing = {
  collectSupplierOverages,
  fromSummary,
  describe,
  PAGE_SIZE,
  MAX_PAGES,
};
